import { Box, Collapse, Dialog, Stack, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { ColorScheme } from "../../Themes/ColorScheme";
import { ApiContext } from "../ApiContext";
import { Spinner } from "../Common/Spinner";
import { DoorUser } from "./DoorUser";

import { ReactComponent as CheckmarkIcon } from "../../Assets/Icons/Checkmark.svg";
import { ReactComponent as CrossIcon } from "../../Assets/Icons/Cross.svg";

import PagingIndicator from "../Common/PagingIndicator";
import { SearchBar } from "../Common/SearchBar";
import SmallButton from "../Common/SmallButton";
import { DoorAccessEdit } from "./DoorAccessEdit";

const DEFAULT_WEEKDAYS = "0123456";

export const ListDoorUsers = ({ door }) => {

	const debug = false;

	const [doorUsers, setDoorUsers] = useState([]);
	const [loading, setLoading] = useState(true);

	const [dialogOpen, setDialogOpen] = useState(false);

	const [requests, setRequests] = useState([]);

	const [pageNumber, setPageNumber] = useState(1);
	const [totalPages] = useState(
		Math.ceil(door.user_count / 10) || 0
	);

	const [search, setSearch] = useState(""); // testing collapse animation

	const { t } = useTranslation();
	const { ApiCall } = useContext(ApiContext);

	const handlePageChange = (newPageNumber) => {
		if (loading) return; // prevent changing page while loading
		setPageNumber(newPageNumber);
	};

	const fetchNewRequests = () => {
		ApiCall("GET", "client/access_requests/" + door.id)
			.then((response) => {
				setRequests(response);
			})
			.catch((error) => {
				if (debug) console.log(error);
			});
	};

	const fetchDoorUsers = () => {
		ApiCall(
			"GET",
			`client/accesses/${door.id}?page=${pageNumber}&search=${search}`
		)
			.then((users) => {
				setDoorUsers(users);
				setLoading(false);

				if (users.length === 0 && search !== "" && pageNumber > 1)
					setPageNumber(pageNumber - 1); // go back to previous page if no results found
			})
			.catch((error) => {
				if (debug) console.log(error);
				setLoading(false);
			});
	};

	const handleFetch = () => {
		fetchDoorUsers();
	};

	const handleAccept = (id) => {
		ApiCall("PUT", "client/access/" + id, {
			access_ends_at: new Date(),
			granted: true,
			start_time: new Date(),
			end_time: new Date(),
			weekdays: DEFAULT_WEEKDAYS,
			access_starts_at: new Date(),
			restrict_times: false
		})
			.then(() => {
				fetchDoorUsers();
				fetchNewRequests();
			})
			.catch((error) => {
				console.log(error);
			});
	};

	const handleReject = (id) => {
		ApiCall("PUT", "client/access/" + id, {
			access_ends_at: new Date(),
			granted: false,
			start_time: new Date(),
			end_time: new Date(),
			weekdays: DEFAULT_WEEKDAYS,
			access_starts_at: new Date(),
			restrict_times: false
		})
			.then(() => {
				fetchDoorUsers();
				fetchNewRequests();
			})
			.catch((error) => console.log(error));
	};

	useEffect(() => {
		if (pageNumber !== 1) setPageNumber(1);
		// Only reset pageNumber, don't trigger fetch here
		// The main useEffect will handle fetching when pageNumber changes
	}, [search]);

	useEffect(() => {
		setDoorUsers([]);
		setLoading(true);

		const debounceTimer = setTimeout(() => {
			setDoorUsers([]);
			fetchDoorUsers();
		}, 300);

		return () => {
			clearTimeout(debounceTimer);
		};

	}, [search, pageNumber]);

	useEffect(() => {
		fetchNewRequests();
	}, []);

	const openDialog = () => {
		setDialogOpen(true);
	};

	const handleDialogClose = () => {
		setDialogOpen(false);
		fetchDoorUsers();
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: "20px"}}>
			{requests.length > 0 && <Typography style={{fontSize: "20px",fontWeight: "700", marginBottom: "0px"}}>{t("doors.door_access_request_label")}</Typography>}
			{requests.map((request) => (
                    <Box key={request.id} style={{ 
						display: "flex",
						flexDirection: "row",
						boxSizing: "border-box",
						borderRadius: "4px",
						alignItems: "center",
						justifyContent: "space-between",
						width: "100%",
						minHeight: "64px",
						border: "1px solid " + ColorScheme.primary,
						backgroundColor: ColorScheme.primary,
						transition: "height 0.3s ease-in-out",
						padding: "20px"
					 }}>
						<div style={{display: "flex", flexDirection: "column"}}>
							<Typography sx={{fontSize: "20px", color: ColorScheme.textPrimary}}>{request.user.name}</Typography>
							<Typography sx={{fontSize: "14px", color: ColorScheme.textPrimary}}>{request.user.email}</Typography>
						</div>
						<Stack
							style={{ justifyContent: "space-between", display: "flex", alifgnSelf: "center" }}
							direction="row"
						>
							<Box
								onClick={() => handleAccept(request.id)}
								sx={{
									width: "25px",
									height: "25px",
									borderRadius: "4px",
									backgroundColor: ColorScheme.secondary,
									border: "1px solid white",
									justifyContent: "center",
									alignItems: "center",
									display: "flex",
									marginRight: "5px",
									cursor: "pointer"
								}}
							>
								<CheckmarkIcon stroke={ColorScheme.primary}/>
							</Box>
							<Box
								onClick={() => handleReject(request.id)}
								style={{
									width: "25px",
									height: "25px",
									borderRadius: "4px",
									backgroundColor: ColorScheme.transparent,
									border: "2px solid " + ColorScheme.secondary,
									justifyContent: "center",
									alignItems: "center",
									display: "flex",
									cursor: "pointer"
								}}
							>
								<CrossIcon stroke={ColorScheme.secondarye} />
							</Box>
						</Stack>
                    </Box>
                ))}
				<div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
				{requests.length > 0 && <Typography style={{fontSize: "20px",fontWeight: "700", marginBottom: "0px"}}>{t("doors.access_rights")}</Typography>}
				<div />
				<SmallButton action={openDialog} text={t("doors.add_new_access")} backgroundColor={ColorScheme.buttonPrimary}></SmallButton>
			</div>
			<Box
				style={{
					display: "flex",
					flexDirection: "column",
					boxSizing: "border-box",
					borderRadius: "4px",
					width: "100%",
					minHeight: "64px",
					margin: "auto",
					border: "1px solid " + ColorScheme.lightGrey,
					backgroundColor: ColorScheme.secondary,
					marginTop: "20px",
					transition: "height 0.3s ease-in-out",
					overflow: "hidden"
				}}
			>
				<SearchBar text={search}
					valueChange={setSearch}/>
				<Collapse
					collapsedSize={64}
					in={!loading}
					easing="ease-in-out"
					timeout={{ enter: 200, exit: 200 }}
				>
					<Box sx={{backgroundColor: ColorScheme.lightGrey, border: "1px solid " + ColorScheme.darkGrey + ColorScheme.opacity10}}>
					{doorUsers.map((user, index) => {
						if (user === undefined) return null;
						const splitWeekdays = (user.weekdays ?? "").split("");

						return (
							<React.Fragment key={user.id}>
								<Box>
									<DoorUser
										splitWeekdays={splitWeekdays}
										access={user}
										refreshUserList={fetchDoorUsers}
										fetchNewData={handleFetch}
									/>
									{/* )} */}
								</Box>

								{index !== doorUsers.length - 1 && (
									<Box
										sx={{
											width: "100%",
											height: "1px",
											backgroundColor: ColorScheme.lightGrey
										}}
									/>
								)}
							</React.Fragment>
						);
					})}
					<div
						style={{
							minHeight: loading ? "64px" : 0,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<div
							style={{
								display:
									loading
										? "flex"
										: "none"
							}}
						>
							<Spinner small={true} color={ColorScheme.primary} />
						</div>

						<div
							style={{
								display:
									!loading &&
									doorUsers.length === 0
										? "flex"
										: "none",
								alignItems: "center",
								padding: "20px"
							}}
						>
							<Typography
								sx={{
									fontSize: "14px",
									fontWeight: "700",
									color: ColorScheme.black
								}}
							>
								{search === ""
									? t("doors.no_access_right")
									: t("doors.no_search_results")}
							</Typography>
						</div>
					</div>
					</Box>
				</Collapse>
			</Box>

			<div style={{width: "100%", display: "flex", justifyContent: "center", alignItems: "center", margin: "20px 0"}}>
				<PagingIndicator maxButtons={search == "" ? 5 : 1} hideTotalPagesCount={search != ""} totalPages={totalPages} currentPage={pageNumber} onPageChange={handlePageChange}/>
			</div>

			<Dialog open={dialogOpen} onClose={handleDialogClose}>
				<DoorAccessEdit
					doorId={door.id}
					data={null}
					handleClick={handleDialogClose}
					closeDialog={handleDialogClose}
				/>
			</Dialog>
		</div>
	);
};
